import type { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import axios from "axios";
import { camelCase } from "lodash/fp";
import { KeycloakInstance } from "keycloak-js";
import { API_URL, IS_DEVELOPMENT } from "../../utils/constants";
import { MINIMUM_UPDATE_TOKEN_VALIDITY_MS } from "../keycloak";
import * as API from "lib/api/types";
import { serializePayload } from "lib/api/utils";
import { APIErrorKey, HTTPErrorKey } from "translations/types";
import { mapKeysDeep, result } from "utils";
import {
  AUTHENTICATION_TIMEOUT_MS,
  withAuthenticationTimeout,
} from "lib/authentication";

interface APIErrorResponseData {
  error?: {
    code?: APIErrorKey;
    message?: string;
  };
}

const interceptError = (err: AxiosError<APIErrorResponseData>): API.ResponseError => {
  let errorKey: APIErrorKey | HTTPErrorKey;
  let errorType: "api" | "http";

  const response = err?.response;

  if (err.message === "Network Error") {
    errorKey = "networkError";
    errorType = "http";
  } else if (response?.data?.error?.code) {
    errorType = "api";
    errorKey = response.data.error.code;
  } else {
    errorType = "http";
    switch (response?.status) {
      case API.HTTPResponse.BadRequest: {
        errorKey = "badRequest";
        break;
      }
      case API.HTTPResponse.Unauthorized: {
        errorKey = "unauthorized";
        break;
      }
      case API.HTTPResponse.Forbidden: {
        errorKey = "forbidden";
        break;
      }
      case API.HTTPResponse.NotFound: {
        errorKey = "notFound";
        break;
      }
      case API.HTTPResponse.Conflict: {
        errorKey = "conflict";
        break;
      }
      case API.HTTPResponse.PreconditionFailed: {
        errorKey = "preconditionFailed";
        break;
      }
      default: {
        errorKey = "serverError";
        console.warn(`Unhandled API response: ${err}`);
      }
    }
  }

  const responseError: API.ResponseError = {
    key: errorKey,
    type: errorType,
    message: response?.data?.error?.message,
    statusCode: response?.status,
    baseResponse: response,
  };

  return responseError;
};

export class APIClient {
  readonly baseUrl: string;
  readonly httpClient: AxiosInstance;
  readonly allowedActions: API.Actions;
  readonly keycloak: KeycloakInstance;

  constructor({ keycloak }: API.ClientConfig) {
    this.baseUrl = API_URL;
    this.keycloak = keycloak;
    this.allowedActions = {
      CHECK_HEALTH: {
        name: "CHECK_HEALTH",
        resource: "health",
        method: "GET",
        createPayload: () => null,
      },
      GET_PROFILE: {
        name: "GET_PROFILE",
        resource: "users/me",
        method: "GET",
        createPayload: () => null,
      },
      GET_PAYMENT_HISTORY: {
        name: "GET_PAYMENT_HISTORY",
        resource: "users/me/payment-history",
        method: "GET",
        createPayload: () => null,
      },
      GET_SIGN_IN_METHODS: {
        name: "GET_SIGN_IN_METHODS",
        resource: "users/me/sign-in-methods",
        method: "GET",
        createPayload: () => null,
      },
      REMOVE_SIGN_IN_METHOD: {
        name: "REMOVE_SIGN_IN_METHOD",
        resource: "users/me/sign-in-methods",
        method: "DELETE",
        createPayload: () => null,
      },
      RESET_PASSWORD: {
        name: "RESET_PASSWORD",
        resource: "users/me/reset-password",
        method: "POST",
        createPayload: () => null,
      },
      UPDATE_PROFILE: {
        name: "UPDATE_PROFILE",
        resource: "users/me",
        method: "PATCH",
        createPayload: (payload: API.UserProfilePayload) => serializePayload(payload),
      },
      CREATE_USER: {
        name: "CREATE_USER",
        resource: "users",
        method: "POST",
        createPayload: (payload: API.UserSignupPayload) => serializePayload(payload),
      },
    };

    this.httpClient = axios.create({
      baseURL: this.baseUrl,
      timeout: AUTHENTICATION_TIMEOUT_MS,
      withCredentials: true,
    });

    this.httpClient.interceptors.response.use(
      (res: AxiosResponse) => res,
      (err: AxiosError<APIErrorResponseData>): Promise<API.ResponseError> =>
        Promise.reject(interceptError(err)),
    );
  }

  private async sendRequest(
    action: API.Action,
    payload?: API.Payload,
  ): Promise<API.Response> {
    // Only refresh the token when the user is already authenticated. Some
    // requests (e.g. signup via createUser) are made while logged out, in
    // which case there is no refresh token and updateToken() would reject with
    // "Unable to update token, no refresh token available." The request itself
    // sends the Authorization header conditionally on keycloak.token below, so
    // unauthenticated requests proceed without a token.
    if (this.keycloak.authenticated) {
      const [tokenErr] = await result(
        withAuthenticationTimeout(
          this.keycloak.updateToken(MINIMUM_UPDATE_TOKEN_VALIDITY_MS),
        ),
      );

      if (tokenErr) {
        if (IS_DEVELOPMENT) console.error(tokenErr);
        return [tokenErr as unknown as API.ResponseError, null];
      }
    }

    const [err, res] = await result(
      this.httpClient.request({
        method: action.method,
        url: `/api/${action.resource}`,
        data: action.createPayload(payload),
        headers: {
          Authorization: this.keycloak.token ? `Bearer ${this.keycloak.token}` : false,
        },
      }),
    );

    if (err) {
      if (IS_DEVELOPMENT) console.error(err);
      return [err as unknown as API.ResponseError, null];
    }

    if (res?.data?.data) {
      return [null, mapKeysDeep(camelCase)(res.data.data)];
    }

    const fallbackErr: API.ResponseError = {
      key: "serverError",
      type: "http",
      baseResponse: res,
    };

    return [fallbackErr, null];
  }

  async checkHealth() {
    const response = await this.sendRequest(this.allowedActions.CHECK_HEALTH);
    return response as API.Response<API.HealthResponse>;
  }

  async getProfile() {
    const response = await this.sendRequest(this.allowedActions.GET_PROFILE);
    return response as API.Response<API.UserProfile>;
  }

  async getPaymentHistory() {
    const response = await this.sendRequest(this.allowedActions.GET_PAYMENT_HISTORY);
    return response as API.Response<API.PaymentHistory>;
  }

  async getSignInMethods() {
    const response = await this.sendRequest(this.allowedActions.GET_SIGN_IN_METHODS);
    return response as API.Response<API.SignInMethods>;
  }

  async removeSignInMethod(removalHandle: string): Promise<API.Response> {
    return this.sendRequest({
      ...this.allowedActions.REMOVE_SIGN_IN_METHOD,
      resource: `users/me/sign-in-methods/${encodeURIComponent(removalHandle)}`,
    });
  }

  async updateProfile(payload: API.UserProfilePayload) {
    const response = await this.sendRequest(
      this.allowedActions.UPDATE_PROFILE,
      payload,
    );
    return response as API.Response<API.UserProfile>;
  }

  async resetPassword(): Promise<API.Response> {
    return this.sendRequest(this.allowedActions.RESET_PASSWORD);
  }

  async createUser(payload: API.UserSignupPayload): Promise<API.Response> {
    return this.sendRequest(this.allowedActions.CREATE_USER, payload);
  }
}
